import type { BotSettings, CalendarSettings, Locale } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../../app/trpc.js";
import { Button, Card, Input, Notice, SectionTitle, Spinner, Textarea } from "../../../ui/index.js";
import { QuestionField, questionRowClass } from "../QuestionField.js";
import { StepNav } from "../StepNav.js";
import { type FieldValue, NUMBER_LIMITS, parseNumber } from "../sources.js";
import type { StepProps } from "../types.js";
import { isDirty, useUnsavedChanges } from "../unsaved.js";

const num = (s: string, id: string): number | null => parseNumber(s, NUMBER_LIMITS[id] ?? [0, 10_000]);

interface Form {
  address: string;
  hoursNote: string;
  answers: Record<string, string>;
  cancelHours: string;
  lateTolerance: string;
  languages: Locale[];
  topics: string;
  autonomy: BotSettings["autonomy"];
  faq: Array<{ question: string; answer: string }>;
}

/**
 * Шаг 4: 10–15 вопросов о правилах салона (список задаёт шаблон ниши). Адрес и ответы идут в знания бота,
 * поздняя отмена и опоздание уходят в правила календаря, языки и «всегда к мастеру» в настройки бота.
 * Кто отвечает (включён, как записывает, сколько ждёт мастера): следующий шаг.
 * Ответы, найденные в источниках владельца на шаге услуг (`answersDraft`), подставлены в форму с пометкой.
 */
export function RulesStep({ salonId, state, setState, next, back }: StepProps) {
  const { t } = useTranslation();
  const calendar = useQuery(trpc.calendar.settings.get.queryOptions({ salonId }));
  const bot = useQuery(trpc.bot.settings.get.queryOptions({ salonId }));
  const [form, setForm] = useState<Form | null>(null);
  /** Снимок формы при открытии: с ним сравниваем, есть ли несохранённые правки. */
  const [initial, setInitial] = useState<Form | null>(null);
  const [fromSources] = useState(() => new Set(Object.keys(state.answersDraft ?? {})));
  useEffect(() => {
    if (form || !calendar.data || !bot.data) return;
    const draft = state.answersDraft ?? {};
    const draftAnswers = Object.fromEntries(
      state.questions
        .filter((q) => q.target === "answers" && typeof draft[q.id as keyof typeof draft] === "string")
        .map((q) => [q.id, draft[q.id as keyof typeof draft] as string]),
    );
    const filled: Form = {
      address: draft.address ?? state.profile.address ?? "",
      hoursNote: draft.hours_note ?? state.profile.hoursNote ?? "",
      answers: { ...Object.fromEntries(Object.entries(state.profile.answers).map(([k, v]) => [k, v ?? ""])), ...draftAnswers },
      cancelHours: String(draft.cancel_hours ?? calendar.data.lateCancelHours),
      lateTolerance: String(draft.late_tolerance ?? calendar.data.lateArrivalToleranceMin),
      languages: draft.languages ?? bot.data.languages,
      topics: (draft.handoff_topics ?? bot.data.alwaysHandoffTopics).join("\n"),
      autonomy: bot.data.autonomy,
      faq: state.profile.faq,
    };
    setForm(filled);
    setInitial(filled);
  }, [form, calendar.data, bot.data, state.profile, state.answersDraft, state.questions]);
  useUnsavedChanges(isDirty(initial, form));

  const saveProfile = useMutation(trpc.onboarding.saveProfile.mutationOptions());
  const saveCalendar = useMutation(trpc.calendar.settings.update.mutationOptions());
  const saveBot = useMutation(trpc.bot.settings.update.mutationOptions());
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<"error" | "tooLong" | null>(null);

  if (!form) return calendar.isError || bot.isError ? <Notice kind="error">{t("common.error")}</Notice> : <Spinner />;
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const cancelHours = num(form.cancelHours, "cancel_hours");
  const lateTolerance = num(form.lateTolerance, "late_tolerance");
  const faqValid = form.faq.every((f) => f.question.trim() && f.answer.trim());
  const valid = cancelHours !== null && lateTolerance !== null && form.languages.length > 0 && faqValid;
  const questions = state.questions.filter((q) => q.id !== "autonomy");
  const asked = new Set(questions.map((q) => q.id));

  const onSave = async () => {
    if (!valid || cancelHours === null || lateTolerance === null) return;
    setSaving(true);
    setFailed(null);
    try {
      const calendarPatch: Partial<CalendarSettings> = {};
      if (asked.has("cancel_hours")) calendarPatch.lateCancelHours = cancelHours;
      if (asked.has("late_tolerance")) calendarPatch.lateArrivalToleranceMin = lateTolerance;
      const botPatch: Partial<BotSettings> = {};
      if (asked.has("languages")) botPatch.languages = form.languages;
      if (asked.has("autonomy")) botPatch.autonomy = form.autonomy;
      if (asked.has("handoff_topics"))
        botPatch.alwaysHandoffTopics = form.topics
          .split("\n")
          .map((x) => x.trim())
          .filter(Boolean)
          .slice(0, 50);
      const [s] = await Promise.all([
        saveProfile.mutateAsync({
          salonId,
          address: form.address.trim() || null,
          hoursNote: form.hoursNote.trim() || null,
          answers: form.answers,
          faq: form.faq.map((f) => ({ question: f.question.trim(), answer: f.answer.trim() })),
        }),
        Object.keys(calendarPatch).length > 0 ? saveCalendar.mutateAsync({ salonId, patch: calendarPatch }) : null,
        Object.keys(botPatch).length > 0 ? saveBot.mutateAsync({ salonId, patch: botPatch }) : null,
      ]);
      setState(s);
      void queryClient.invalidateQueries(trpc.calendar.settings.pathFilter());
      void queryClient.invalidateQueries(trpc.bot.settings.pathFilter());
      next({ saved: true });
    } catch (e) {
      // Весь текст профиля уходит в каждый ответ бота, у него общий потолок (`PROFILE_BUDGET_CHARS` на сервере).
      const reason = (e as { data?: { details?: { reason?: unknown } | null } } | null)?.data?.details?.reason;
      setFailed(reason === "too_long" ? "tooLong" : "error");
    } finally {
      setSaving(false);
    }
  };

  /** Значение вопроса в форме шага и его запись обратно: часть ответов живёт в своих полях (календарь, бот, профиль). */
  const fieldValue = (id: string): FieldValue => {
    if (id === "address") return form.address;
    if (id === "hours_note") return form.hoursNote;
    if (id === "cancel_hours") return form.cancelHours;
    if (id === "late_tolerance") return form.lateTolerance;
    if (id === "languages") return form.languages;
    if (id === "handoff_topics") return form.topics;
    if (id === "autonomy") return form.autonomy;
    return form.answers[id] ?? "";
  };
  const setFieldValue = (id: string, v: FieldValue) => {
    const str = typeof v === "string" ? v : "";
    if (id === "address") set({ address: str });
    else if (id === "hours_note") set({ hoursNote: str });
    else if (id === "cancel_hours") set({ cancelHours: str });
    else if (id === "late_tolerance") set({ lateTolerance: str });
    else if (id === "languages") set({ languages: Array.isArray(v) ? v : [] });
    else if (id === "handoff_topics") set({ topics: str });
    else if (id === "autonomy") set({ autonomy: str as BotSettings["autonomy"] });
    else set({ answers: { ...form.answers, [id]: str } });
  };

  return (
    <div>
      <p className="mb-4 text-slate-600">{t("onb.rules.intro")}</p>
      <Card className="@container space-y-5">
        {questions.map((q) => (
          <div key={q.id} className={questionRowClass}>
            <QuestionField q={q} value={fieldValue(q.id)} onChange={(v) => setFieldValue(q.id, v)} fromSources={fromSources.has(q.id)} />
          </div>
        ))}
      </Card>

      <SectionTitle>{t("onb.rules.faq")}</SectionTitle>
      <p className="mb-2 text-sm text-slate-500">{t("onb.rules.faqHint")}</p>
      <div className="space-y-2">
        {form.faq.map((f, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: вопросы без id, порядок меняется только удалением
          <Card key={i} className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={f.question}
                onChange={(e) => set({ faq: form.faq.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)) })}
                placeholder={t("onb.rules.question")}
                aria-label={t("onb.rules.question")}
                className="py-2"
              />
              <button
                type="button"
                className="px-2 text-xl text-slate-400"
                aria-label={t("common.remove")}
                onClick={() => set({ faq: form.faq.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
            <Textarea
              rows={2}
              value={f.answer}
              onChange={(e) => set({ faq: form.faq.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)) })}
              placeholder={t("onb.rules.answer")}
              aria-label={t("onb.rules.answer")}
            />
          </Card>
        ))}
      </div>
      <Button variant="ghost" className="mt-2 w-full" onClick={() => set({ faq: [...form.faq, { question: "", answer: "" }] })}>
        + {t("onb.rules.addFaq")}
      </Button>

      {!valid && <Notice kind="error">{t("onb.rules.invalid")}</Notice>}
      {failed && <Notice kind="error">{failed === "tooLong" ? t("onb.rules.tooLong") : t("common.error")}</Notice>}
      <StepNav onBack={back} onNext={() => void onSave()} nextLabel={t("onb.rules.save")} disabled={!valid} busy={saving} />
    </div>
  );
}
